import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Equipment, Insurance, QuoteModel } from '@bytel/bytel-sales';
import {
    LINKED_PRODUCT_TYPE,
    OPTION_STATUS,
    PAYMENT_MODE,
    ProductService,
    QuoteContextRenewalModel,
    UserOption,
    UserService,
} from '@bytel/checkout';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';
import { TrustHtmlPipe } from '@base/common/pipes/trustHtml.pipe';
import { PriceComponent } from '@base/trilogy/price/price.component';

import { CmsService } from '@services/cms.service';
import { ConfigurationService } from '@services/configuration.service';
import { HandleErrorService } from '@services/handle-error.service';
import { MainCartService } from '@services/main-cart.service';
import { StepperService } from '@services/stepper.service';

import { InsuranceCms } from '@interfaces/directus-cms.interface';

import { Subscription } from 'rxjs';

import { CapitalizeFirstLetterPipe } from '@shared/pipes/capitalize-first-letter.pipe';
import { MandatoryFieldComponent } from '~/src/app/shared/mandatory-field/mandatory-field.component';

@Component({
    selector: 'checkout-insurances',
    templateUrl: './insurances.component.html',
    styleUrl: 'insurances.component.scss',
    standalone: true,
    imports: [ReactiveFormsModule, DataCyDirective, TrustHtmlPipe, PriceComponent, CapitalizeFirstLetterPipe, MandatoryFieldComponent],
})
export class InsurancesComponent implements OnInit, OnChanges, OnDestroy {
    public static PDF_LINK: string = '/static/pdf/assurance_mobile.pdf';

    @Input() public equipment: Equipment;
    @Input() public cartForm: FormGroup;
    @Input() public quoteIndex: number = 0;

    public insuranceForm: FormGroup<{
        choice: FormControl<boolean | null>;
    }>;
    public insurance: Insurance;
    public knowMoreUrl: string;
    public isRenewal: boolean;
    public showInsurance: boolean = true;
    public insuranceCmsData: InsuranceCms;
    public showValidationErrors: boolean = false;

    protected showLegals: boolean = false;
    private validationSubscription: Subscription;

    constructor(
        private cartService: MainCartService,
        private userService: UserService,
        private formBuilder: FormBuilder,
        private configurationService: ConfigurationService,
        private productService: ProductService,
        private handleErrorService: HandleErrorService,
        private cmsService: CmsService,
        private stepperService: StepperService,
    ) {
        this.knowMoreUrl = `${this.configurationService.getData()?.global.origin}${InsurancesComponent.PDF_LINK}`;
    }

    public ngOnInit(): void {
        this.cmsService.getInsuranceData().subscribe((data) => {
            this.insuranceCmsData = data;
        });

        this.validationSubscription = this.stepperService.showValidationErrorsSubject.subscribe(() => {
            if (this.insuranceForm && this.insuranceForm.get('choice')?.invalid) {
                this.showValidationErrors = true;
            }
        });
    }

    public ngOnChanges(): void {
        if (this.equipment) {
            this.productService
                .getLinkedProductsByType<Insurance>(this.equipment, LINKED_PRODUCT_TYPE.RELATED, 'Insurance')
                .subscribe((insurances) => {
                    const quote: QuoteModel = this._getQuote();
                    this.insurance = insurances.length ? insurances[0] : null;

                    this.isRenewal = quote.isRenewal();
                    this.showInsurance = !!this.insurance;

                    this._handleInsuranceData(quote);
                    this._handleInsurance();
                });
        } else {
            this._removeForm();
        }
    }

    public ngOnDestroy(): void {
        this._removeForm();
        if (this.validationSubscription) {
            this.validationSubscription.unsubscribe();
        }
    }

    public addInsurance(): void {
        // Link with Equipment, add insurance and save
        this.cartService.addProduct(this.insurance).subscribe(
            () => {
                this.equipment.insurance = this.insurance;
                this._saveInsurance(true);
            },
            (error: Error) => this.handleErrorService.handleError(error),
        );
    }

    public removeInsurance(): void {
        const cartInsurances: Insurance[] = this._getQuote().getProductsByType('Insurance');
        const matchingInsurance: Insurance = cartInsurances.find(
            (cartInsurance: Insurance) => cartInsurance.gencode === this.insurance.gencode,
        );

        if (matchingInsurance) {
            this.cartService.removeProduct(matchingInsurance, this.quoteIndex).subscribe(() => {
                delete this.equipment.insurance;
                this._saveInsurance(false);
            });
        }
    }

    private _refresh(): void {
        const quote: QuoteModel = this._getQuote();

        if (quote) {
            const storedInsurances = quote?.forms?.insurances;
            const productInsurance = storedInsurances ? storedInsurances[this.equipment.gencode] : null;
            this.insuranceForm.patchValue({ choice: productInsurance }, { onlySelf: false });
        }
    }

    private _saveInsurance(value: boolean): void {
        const data = {
            insurances: {
                [this.equipment.gencode]: value,
            },
        };
        this._getQuote().addFormData(data);
        this.cartService.save();
    }

    private _removeForm(): void {
        if (this.cartForm.contains('insurance')) {
            this.cartForm.removeControl('insurance');
        }
    }

    private _handleInsuranceData(quote: QuoteModel): void {
        if (this.showInsurance && this.isRenewal) {
            const isDirectDebitMode: boolean = this.userService.user?.paymentMethod === PAYMENT_MODE.PRELEVEMENT;
            let hasOption: boolean = false;
            if (isDirectDebitMode) {
                const noInsuranceList: string[] = ['11662', '11663', '11664', '11665'];
                const userOptions: UserOption[] = (quote.context as QuoteContextRenewalModel).getData().options;
                hasOption =
                    userOptions?.length > 0 &&
                    userOptions?.some(
                        (userOption) =>
                            noInsuranceList.includes(userOption.id) && userOption.statut === OPTION_STATUS.SUBSCRIBED,
                    );
            }
            this.showInsurance = isDirectDebitMode && !hasOption;
        }
    }

    private _handleInsurance(): void {
        if (this.showInsurance) {
            if (!this.insuranceForm) {
                this.insuranceForm = this.formBuilder.group({
                    choice: new FormControl(null, Validators.required),
                });
                this.cartForm.addControl('insurance', this.insuranceForm);
                this._refresh();
                this.insuranceForm.get('choice').valueChanges.subscribe((value: boolean) => {
                    if (value !== null) {
                        this.showValidationErrors = false;
                    }
                    if (value) {
                        this.addInsurance();
                        this.showLegals = true;
                    } else {
                        this.removeInsurance();
                        this.showLegals = false;
                    }
                });
            } else {
                this._refresh();
            }
        } else {
            this._removeForm();
        }
    }

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }
}
